import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { HexAlphaColorPicker } from 'react-colorful'
import { InputGroup, InputGroupText } from 'reactstrap'

// Helper: Memastikan format hex selalu valid untuk HexAlphaColorPicker
const normalizeHexAlpha = (color) => {
  if (!color) return '#006386FF'
  const clean = color.replace(/^#/, '').toUpperCase()
  if (/^[0-9A-F]{8}$/.test(clean)) return `#${clean}`
  if (/^[0-9A-F]{6}$/.test(clean)) return `#${clean}FF`
  if (/^[0-9A-F]{3}$/.test(clean)) {
    const full = clean
      .split('')
      .map((c) => c + c)
      .join('')
    return `#${full}FF`
  }
  return '#006386FF'
}

// Helper: Menghitung persentase opasitas dari hex 8 digit
const getOpacityPercent = (color) => {
  if (!color) return 100
  const clean = color.replace(/^#/, '')
  if (clean.length === 8) {
    const alphaInt = parseInt(clean.slice(6, 8), 16)
    if (!isNaN(alphaInt)) return Math.round((alphaInt / 255) * 100)
  }
  return 100
}

// Helper: Mengubah nilai alpha pada warna saat ini
const setAlphaPercent = (color, percent) => {
  const normalized = normalizeHexAlpha(color)
  const baseHex = normalized.slice(1, 7) // 6 karakter RGB
  const alphaVal = Math.round((Math.max(0, Math.min(100, percent)) / 100) * 255)
  const alphaHex = alphaVal.toString(16).padStart(2, '0').toUpperCase()
  return `#${baseHex}${alphaHex}`
}

const DEFAULT_PRESETS = [
  { label: 'Solid (100%)', val: 100 },
  { label: '75%', val: 75 },
  { label: '50%', val: 50 },
  { label: '25%', val: 25 },
  { label: 'Transparan (0%)', val: 0 },
]

const PMBColorPicker = ({
  id = 'pmb-color-picker',
  value = '',
  onChange,
  disabled = false,
  placeholder = 'Contoh: 006386 atau 00638680',
  className = '',
  showPresets = true,
  showInput = true,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  // Tutup popover jika user klik di luar komponen
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const currentColor = normalizeHexAlpha(value)
  const opacity = getOpacityPercent(value)
  const cleanDisplayHex = (value || '').replace(/^#/, '').toUpperCase()

  const handlePickerChange = (newColor) => {
    if (onChange) {
      onChange(newColor.toUpperCase())
    }
  }

  const handleInputChange = (e) => {
    const raw = e.target.value
    // Mengizinkan hapus full sampai kosong!
    if (!raw) {
      if (onChange) onChange('')
      return
    }

    // Hanya izinkan karakter hex: 0-9, a-f, A-F (otomatis buang '#' jika user paste atau ketik)
    const cleanHex = raw.replace(/[^0-9A-Fa-f]/g, '').toUpperCase().slice(0, 8)
    if (onChange) {
      onChange(cleanHex ? `#${cleanHex}` : '')
    }
  }

  const handlePresetClick = (percent) => {
    const updated = setAlphaPercent(value || '#006386', percent)
    if (onChange) onChange(updated)
  }

  return (
    <div
      ref={containerRef}
      className={`pmb-color-picker position-relative ${className}`}
    >
      <div className="d-flex align-items-center gap-2">
        {/* Swatch Box dengan preview transparansi motif catur */}
        <button
          type="button"
          id={id}
          disabled={disabled}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          title="Klik untuk membuka palet warna"
          aria-label="Pilih warna"
          style={{
            position: 'relative',
            width: '42px',
            height: '38px',
            borderRadius: '6px',
            border: '1px solid #ced4da',
            cursor: disabled ? 'not-allowed' : 'pointer',
            flexShrink: 0,
            overflow: 'hidden',
            padding: 0,
            backgroundImage:
              'linear-gradient(45deg, #e0e0e0 25%, transparent 25%), linear-gradient(-45deg, #e0e0e0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e0e0e0 75%), linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)',
            backgroundSize: '8px 8px',
            backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
            outline: 'none',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: value ? normalizeHexAlpha(value) : 'transparent',
            }}
          />
        </button>

        {/* Input Text dengan prefix # (bisa dihapus full, hanya menerima hex, max 8 char) */}
        {showInput && (
          <InputGroup style={{ flex: 1 }}>
            <InputGroupText
              className="bg-light text-muted fw-bold px-2"
              style={{ fontSize: '14px', borderColor: '#ced4da' }}
            >
              #
            </InputGroupText>
            <input
              id={`${id}-input`}
              type="text"
              value={cleanDisplayHex}
              onChange={handleInputChange}
              className="form-control text-uppercase"
              placeholder={placeholder}
              maxLength={8}
              disabled={disabled}
              autoComplete="off"
            />
          </InputGroup>
        )}
      </div>

      {/* Popover Color Picker dari react-colorful */}
      {isOpen && (
        <div
          className="pmb-color-picker-popover shadow-lg border rounded-3 p-3 bg-white position-absolute"
          style={{
            zIndex: 1050,
            top: 'calc(100% + 6px)',
            left: 0,
            width: '240px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-xs fw-semibold text-dark">Pilih Warna & Transparansi</span>
            <span className="text-xs text-muted">
              {opacity}% {opacity < 100 ? '(Transparan)' : '(Solid)'}
            </span>
          </div>

          {/* HexAlphaColorPicker dari react-colorful (output 8-digit HEX) */}
          <div className="d-flex justify-content-center">
            <HexAlphaColorPicker
              color={currentColor}
              onChange={handlePickerChange}
              style={{ width: '100%', height: '180px' }}
            />
          </div>

          {/* Preset Opasitas / Transparansi Cepat */}
          {showPresets && (
            <div className="mt-2 pt-2 border-top">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="text-xs text-muted" style={{ fontSize: '11px' }}>
                  Pilihan Cepat Opasitas:
                </span>
                <span className="text-xs fw-bold text-primary" style={{ fontSize: '11px' }}>
                  {opacity}%
                </span>
              </div>
              <div className="d-flex flex-wrap gap-1">
                {DEFAULT_PRESETS.map((preset) => (
                  <button
                    key={preset.val}
                    type="button"
                    className={`btn btn-xs py-0 px-2 rounded-pill ${
                      opacity === preset.val
                        ? 'btn-primary text-white'
                        : 'btn-outline-secondary'
                    }`}
                    style={{ fontSize: '10px', height: '20px' }}
                    onClick={() => handlePresetClick(preset.val)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tombol Tutup */}
          <div className="mt-2 pt-2 border-top d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-sm btn-light text-xs px-3 py-1"
              onClick={() => setIsOpen(false)}
            >
              Selesai
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

PMBColorPicker.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  showPresets: PropTypes.bool,
  showInput: PropTypes.bool,
}

export default PMBColorPicker
